import apiConfig from '../config';
import { HTTP_STATUS, ERROR_MESSAGES } from '../constants';

class BaseService {
  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      body = null,
      headers = {},
      params = {},
      useCache = false,
      cacheTTL = null
    } = options;

    const cacheKey = useCache ? apiConfig.getCacheKey(endpoint, params) : null;
    
    if (useCache && cacheKey) {
      const cached = apiConfig.getFromCache(cacheKey);
      if (cached) return cached;
    }

    const url = apiConfig.buildURL(endpoint, params);
    const requestHeaders = apiConfig.getHeaders(headers);

    // Debug: Log request details for material category creation
    if (endpoint.includes('/materials/categories') && method === 'POST') {
      console.log('BaseService POST request:', {
        url,
        method,
        headers: requestHeaders,
        hasAuthHeader: !!requestHeaders.Authorization
      });
    }

    // Debug: Log idea requests
    if (endpoint.includes('/ideas')) {
      console.log('BaseService Idea Request:', {
        endpoint,
        url,
        method,
        body: body,
        hasAuthHeader: !!requestHeaders.Authorization
      });
    }

    const config = {
      method,
      headers: requestHeaders
    };

    if (body && method !== 'GET') {
      config.body = JSON.stringify(body);
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), apiConfig.getDefaultTimeout());

      const response = await fetch(url, {
        ...config,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await this.handleError(response);
      }

      const data = await response.json();

      if (useCache && cacheKey) {
        apiConfig.setCache(cacheKey, data, cacheTTL);
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error(ERROR_MESSAGES.TIMEOUT_ERROR);
      }
      throw error;
    }
  }

  async handleError(response) {
    const status = response.status;
    let errorMessage = ERROR_MESSAGES.UNKNOWN_ERROR;

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (e) {
      switch (status) {
        case HTTP_STATUS.UNAUTHORIZED:
          errorMessage = ERROR_MESSAGES.UNAUTHORIZED;
          break;
        case HTTP_STATUS.FORBIDDEN:
          errorMessage = ERROR_MESSAGES.FORBIDDEN;
          break;
        case HTTP_STATUS.NOT_FOUND:
          errorMessage = ERROR_MESSAGES.NOT_FOUND;
          break;
        case HTTP_STATUS.UNPROCESSABLE_ENTITY:
          errorMessage = ERROR_MESSAGES.VALIDATION_ERROR;
          break;
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          errorMessage = ERROR_MESSAGES.SERVER_ERROR;
          break;
      }
    }

    const error = new Error(errorMessage);
    error.status = status;
    throw error;
  }

  async get(endpoint, params = {}, options = {}) {
    return this.request(endpoint, { method: 'GET', params, ...options });
  }

  async post(endpoint, body = {}, options = {}) {
    return this.request(endpoint, { method: 'POST', body, ...options });
  }

  async put(endpoint, body = {}, options = {}) {
    return this.request(endpoint, { method: 'PUT', body, ...options });
  }

  async patch(endpoint, body = {}, options = {}) {
    return this.request(endpoint, { method: 'PATCH', body, ...options });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', ...options });
  }

  async uploadFile(endpoint, file, fieldName = 'file', additionalData = {}) {
    const formData = new FormData();
    formData.append(fieldName, file);

    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    const url = apiConfig.getFullAPIURL(endpoint);
    const headers = {};
    const token = apiConfig.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  async postFormData(endpoint, formData) {
    const url = apiConfig.getFullAPIURL(endpoint);
    const headers = {};
    const token = apiConfig.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  async putFormData(endpoint, formData) {
    const url = apiConfig.getFullAPIURL(endpoint);
    const headers = {};
    const token = apiConfig.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: formData
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }

  async deleteFormData(endpoint, formData) {
    const url = apiConfig.getFullAPIURL(endpoint);
    const headers = {};
    const token = apiConfig.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body: formData
    });

    if (!response.ok) {
      await this.handleError(response);
    }

    return response.json();
  }
}

export default new BaseService();
